import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { requestOperation, unwrapData } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { productReleases } from './releases';

export function useProductUpdates(markSeen = false) {
  const user = useAuthStore(state => state.user);
  const client = useQueryClient();
  const key = ['product-updates', user?.id, user?.organizationId];
  const latest = productReleases[0]?.id;
  const query = useQuery({ queryKey: key, enabled: Boolean(user), staleTime: 60000,
    queryFn: async () => unwrapData<{ latestReleaseId: string; lastSeenReleaseId?: string }>(await requestOperation('GET /api/v1/users/me/product-updates')) });
  useEffect(() => {
    if (!markSeen || !user || !query.data || query.data.latestReleaseId !== latest || query.data.lastSeenReleaseId === latest) return;
    let active = true;
    let pending = false;
    const acknowledge = async () => {
      if (document.visibilityState !== 'visible' || pending) return;
      pending = true;
      try {
        const response = await requestOperation<{ data: { latestReleaseId: string; lastSeenReleaseId?: string } }>('PATCH /api/v1/users/me/product-updates', { body: { releaseId: latest } });
        if (active) client.setQueryData(key, unwrapData(response));
      } catch { /* Leave the badge unread; retry on a later visit or visibility change. */ }
      finally { pending = false; }
    };
    void acknowledge();
    document.addEventListener('visibilitychange', acknowledge);
    return () => { active = false; document.removeEventListener('visibilitychange', acknowledge); };
  }, [markSeen, user?.id, user?.organizationId, latest, query.data, client]);
  return { unread: Boolean(query.data && query.data.latestReleaseId === latest && query.data.lastSeenReleaseId !== latest) };
}
